import moment from "moment";
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import { useKeycloak } from "../../components/keycloak-provider";
import { availableLocales, useLocaleProvider } from "../../components/locale-provider";
import { useProperty } from "../../components/property-provider";
import { SearchBarTailwind } from "../../components/search-bar-tailwind";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useWhiteLabel } from "../../components/white-label-provider";
import { LINK_TYPES } from "../../modules/witadmin/Common/constants";
import { SVGMenu } from "./assets";
import { NavMenu } from "./navMenu";
import "moment/locale/es";
import { useRoleChecker } from "../../components/authorization-verifier";
import { AI_USER, EMAIL_ADMIN } from "../../lib/roles";
import AIAssistant from "../../components/ai-assistant/ai-assistant-sidebar";

export interface PropertySelectorProps {
  isWitAdmin: boolean | undefined;
  userProfile?: string;
}

const messages = defineMessages({
  manuals: {
    id: "menu.manuals",
    defaultMessage: "Manuals",
  },
});

export function PropertySelector({ isWitAdmin, userProfile }: PropertySelectorProps) {
  console.debug("PropertySelector", isWitAdmin);
  const keycloak = useKeycloak();
  const history = useHistory();
  const localeProvider = useLocaleProvider();
  const property = useProperty();
  const userPropertyTickersList = useUserPropertyTickers();
  const whiteLabel = useWhiteLabel();
  const propertyInfo = property.info;
  const isAIUser = useRoleChecker([AI_USER]);
  const [propertyTickerOptions, setPropertyTickerOptions] = useState<any>([]);

  const handleChange = useCallback(
    (option: string) => {
      //For now, we try to take the user to the same section on the new property
      history.push(`/${option}/reservations`);
    },
    [history]
  );

  const handleLogout = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      keycloak.logout();
    },
    [keycloak]
  );

  useEffect(() => {
    const fetchPropertyTickers = async () => {
      const newDataList = userPropertyTickersList.map(option => ({ value: option, label: option }));
      setPropertyTickerOptions(newDataList);
    };

    fetchPropertyTickers();
  }, [userPropertyTickersList]);

  const changeLanguage = useCallback(
    (lang: { id: string; label: string; code: string; locale: string }) => {
      localeProvider.setLocale(lang.code);
      moment.locale(lang.locale);
    },
    [localeProvider]
  );

  const urlKnowledgeDatabase = useMemo(() => {
    const link = whiteLabel?.externalSiteConfigurations.find(
      link => link.externalSiteType === LINK_TYPES.KNOWLEDGE_DATABASE
    );
    return link?.url;
  }, [whiteLabel]);

  return (
    <div className="flex gap-[20px] justify-end items-center">
      <div className="flex flex-col truncate border-r-[2px] border-[#2E3862] h-[50px] justify-content-center pr-[20px]">
        <span className="font-semibold text-[#2E3862] text-lg truncate">{propertyInfo.ticker}</span>
        {isWitAdmin && (
          <div className="text-xs text-end font-normal text-[#979CB1]">
            <div>{propertyInfo.partner}</div>
          </div>
        )}
      </div>

      {isAIUser ? (
        <div className="flex justify-end items-center">
          <AIAssistant />
        </div>
      ) : null}

      <div className="flex justify-end items-center">
        <SearchBarTailwind data={propertyTickerOptions} handleSelectOption={handleChange} />
      </div>

      <NavMenu title={SVGMenu["HAMBURGER"]}>
        <div
          id="dropdownLeft"
          className="z-10 fixed mx-3 mt-1 right-0 bg-white divide-y divide-gray-100 rounded-lg shadow">
          <ul className="p-3 text-sm text-gray-600" aria-labelledby="dropdownLeftButton">
            <li className="flex items-center p-2 hover:bg-[#F9F9FA]">
              <span>{SVGMenu.PROFILE}</span>
              <span className="text-[#5C5C5C] ml-2">
                {userProfile && <span className="font-semibold">{userProfile}</span>}
              </span>
            </li>
            <li
              className="flex items-center p-2 hover:bg-[#F9F9FA]"
              onClick={() => window.open(urlKnowledgeDatabase, "_blank")}>
              <span>{SVGMenu.DOCS}</span>
              <span className="text-[#5C5C5C] ml-2">
                <FormattedMessage {...messages.manuals} />
              </span>
            </li>
            {availableLocales
              .filter(locale => locale.code !== localeProvider.currentLocale)
              .map(it => (
                <li
                  key={it.code}
                  className="flex items-center p-2 hover:bg-[#F9F9FA]"
                  onClick={() => changeLanguage(it)}>
                  <span>{SVGMenu.LANG}</span>
                  <span className="text-[#5C5C5C] ml-2 hover:underline hover:text-[#2E3862]">
                    {it.label}
                  </span>
                </li>
              ))}
            <li className="flex items-center p-2 hover:bg-[#F9F9FA]" onClick={handleLogout}>
              <span>{SVGMenu.LOGOUT}</span>
              <span className="text-[#5C5C5C] ml-2 hover:underline hover:text-[#2E3862]">
                <FormattedMessage {...{ id: "header.logout", defaultMessage: "exit" }} />
              </span>
            </li>
          </ul>
        </div>
      </NavMenu>
    </div>
  );
}
